import React, { useEffect, useState } from 'react';
import { CalendarOff } from 'lucide-react';
import { useLang, tx } from '../context/LanguageContext';

// Fermeture estivale 2026. FIN = premier jour de RÉOUVERTURE (borne exclusive).
// Pour désactiver hors période : mettre DEBUT = FIN.
const CONGES = { DEBUT: '2026-08-10', FIN: '2026-08-24' };

// "2026-08-10" → 10 août 2026 à 00:00 heure LOCALE.
// new Date("2026-08-10") serait lu en UTC, soit 02:00 à Paris en été.
function parseLocalDay(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function isVacationActive(now = new Date()) {
  const debut = parseLocalDay(CONGES.DEBUT);
  const fin = parseLocalDay(CONGES.FIN);
  return now >= debut && now < fin;
}

// Textes portés ici plutôt que dans i18n/translations.js : ils sont SAISONNIERS
// (modifiés ou retirés en bloc), les isoler évite de polluer le fichier de
// traductions permanent.
//
// Fonction PURE et exportée : ce dépôt n'a pas @testing-library/react (son seul
// test porte sur une fonction pure), les six langues sont donc vérifiables sans
// rendu et sans ajouter de dépendance de test.
export function vacationTexts(lang) {
  return {
    titre: tx(lang, "Congés d'été", 'Summer break', {
      es: 'Vacaciones de verano',
      de: 'Betriebsferien',
      it: 'Chiusura estiva',
      pt: 'Férias de verão',
    }),
    message: tx(
      lang,
      'Fermeture du 10 au 23 août. Le support des clients sous contrat reste assuré par téléphone au 01 83 62 33 27. Les interventions planifiées reprennent le lundi 24 août.',
      'Closed from 10 to 23 August. Support for customers under contract remains available by phone on +33 1 83 62 33 27. Scheduled work resumes on Monday 24 August.',
      {
        es: 'Cerrado del 10 al 23 de agosto. El soporte para clientes con contrato sigue disponible por teléfono en el +33 1 83 62 33 27. Las intervenciones programadas se reanudan el lunes 24 de agosto.',
        de: 'Geschlossen vom 10. bis 23. August. Der Support für Kunden mit Vertrag bleibt telefonisch unter +33 1 83 62 33 27 erreichbar. Geplante Einsätze werden ab Montag, dem 24. August fortgesetzt.',
        it: "Chiuso dal 10 al 23 agosto. L'assistenza per i clienti con contratto resta disponibile per telefono al +33 1 83 62 33 27. Gli interventi programmati riprendono lunedì 24 agosto.",
        pt: 'Encerrado de 10 a 23 de agosto. O suporte aos clientes com contrato mantém-se disponível por telefone através do +33 1 83 62 33 27. As intervenções programadas recomeçam na segunda-feira, 24 de agosto.',
      }
    ),
  };
}

export default function VacationBanner({ now }) {
  const { lang } = useLang();
  // Rendu après montage : index.js utilise hydrateRoot sur du HTML pré-rendu
  // par react-snap, qui ne contient pas le bandeau.
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const apercu = new URLSearchParams(window.location.search).has('apercu-conges');
    setVisible(apercu || isVacationActive(now));
  }, [now]);

  if (!visible) return null;

  const { titre, message } = vacationTexts(lang);

  return (
    // ⚠️ PersonaSwitcher (fixed, z-60, ~72px) ET Navbar (fixed, z-50, 80px) sont
    // HORS DU FLUX : sans marge, ce bandeau se place à top:0 et disparaît
    // intégralement DERRIÈRE elles — rendu, mais invisible. Même calcul que le
    // bandeau d'information de PrendreRdv.js (l.172), qui règle le même cas.
    <div
      role="status"
      data-testid="vacation-banner"
      className="bg-surface-card border-b border-meteris-blue/30 mt-[calc(var(--persona-h,72px)+80px)]"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3">
        <CalendarOff className="w-4 h-4 shrink-0 text-meteris-blue" aria-hidden="true" />
        <p className="text-xs sm:text-sm leading-snug text-slate-200">
          <span className="font-semibold text-white">{titre}</span>
          <span className="hidden sm:inline"> — </span>
          <span className="block sm:inline text-slate-300">{message}</span>
        </p>
      </div>
    </div>
  );
}
